import React, { useState } from 'react';
import axios from 'axios';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Check, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { FlowAttachInput } from './FlowAttachments';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Statuses in dropdown — full list (all 10) with colour tokens.
const EDITABLE_STATUSES = [
  { value: 'pedido_chamada', label: 'Pedido de Chamada', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'validacao_documentos', label: 'Validação de Documentos', className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  { value: 'chamada_efectuada', label: 'Chamada Efectuada', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'nao_atende', label: 'Não Atende', className: 'bg-rose-100 text-rose-700 border-rose-300' },
  { value: 'esclarecimentos', label: 'Esclarecimentos', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'pendente_documentos', label: 'Pendente de Documentos', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'registado_comercializadora', label: 'Registado na Comercializadora', className: 'bg-teal-100 text-teal-700 border-teal-200' },
  { value: 'concluido', label: 'Concluído', className: 'bg-[#B8E02A]/20 text-[#7BA31D] border-[#B8E02A]/30' },
  { value: 'anulado_comercializadora', label: 'Anulado pela Comercializadora', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'anulado', label: 'Anulado', className: 'bg-red-100 text-red-700 border-red-200' },
];

// States that require a motivo (a textarea modal pops up before saving).
const REASON_REQUIRED_STATES = new Set([
  'anulado',
  'anulado_comercializadora',
  'pendente_documentos',
  'esclarecimentos',
]);

const STATUS_MAP = EDITABLE_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});

export const canEditStatusInline = (user, hasPerm) => {
  if (!user) return false;
  if (['super_admin', 'gestor'].includes(user.role)) return true;
  if (user.role === 'admin' && hasPerm('editar_estado_inline')) return true;
  return false;
};

export const InlineStatusEditor = ({ contract, onUpdated }) => {
  const { user, hasPerm } = useAuth();
  const allowed = canEditStatusInline(user, hasPerm);

  const [draft, setDraft] = useState(contract.status);
  const [saving, setSaving] = useState(false);
  const [motivoOpen, setMotivoOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [motivoFiles, setMotivoFiles] = useState([]);

  if (!allowed) {
    return <StatusBadge status={contract.status} />;
  }

  const dirty = draft !== contract.status;
  const current = STATUS_MAP[draft] || { label: draft, className: 'bg-slate-100 text-slate-700 border-slate-200' };
  const needsReason = REASON_REQUIRED_STATES.has(draft);

  const persist = async (finalMotivo) => {
    setSaving(true);
    try {
      const body = { status: draft };
      if (finalMotivo) body.motivo_estado = finalMotivo;
      await axios.put(`${API_URL}/contracts/${contract.id}`, body);
      // Also upload any attachments as flow_attachment linked to the new state
      if (motivoFiles.length > 0) {
        const fd = new FormData();
        fd.append('content', finalMotivo ? `Anexos para "${current.label}": ${finalMotivo}` : `Anexos — ${current.label}`);
        motivoFiles.forEach(f => fd.append('files', f));
        try { await axios.post(`${API_URL}/contracts/${contract.id}/flow-comment`, fd); } catch (e) { console.error(e); }
      }
      toast.success('Estado atualizado');
      onUpdated?.(contract.id, draft, finalMotivo);
      setMotivoOpen(false);
      setMotivo('');
      setMotivoFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao atualizar estado');
      setDraft(contract.status);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (e) => {
    e?.stopPropagation?.();
    if (!dirty) return;
    if (needsReason) {
      setMotivo('');
      setMotivoOpen(true);
      return;
    }
    persist(null);
  };

  const handleConfirmMotivo = () => {
    const txt = motivo.trim();
    if (!txt) {
      toast.error('Por favor indica o motivo');
      return;
    }
    persist(txt);
  };

  const handleCancel = (e) => {
    e?.stopPropagation?.();
    setDraft(contract.status);
  };

  return (
    <>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Select value={draft} onValueChange={setDraft} disabled={saving}>
          <SelectTrigger
            className={`!h-auto !w-auto !py-1 !px-3 !rounded-full !border !shadow-none !text-xs !font-semibold !gap-1.5 hover:opacity-90 focus:ring-2 focus:ring-offset-1 focus:ring-[#2D3E6F]/40 ${current.className}`}
            data-testid={`inline-status-select-${contract.id}`}
          >
            <SelectValue>
              <span className="whitespace-nowrap">{current.label}</span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {EDITABLE_STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value} className="text-xs py-1.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${s.className}`}>
                  {s.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {dirty && (
          <>
            <Button
              size="icon"
              variant="outline"
              className="h-6 w-6 rounded-full bg-emerald-50 border-emerald-300 hover:bg-emerald-100"
              onClick={handleSave}
              disabled={saving}
              data-testid={`inline-status-save-${contract.id}`}
              title="Guardar"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 text-emerald-700" />}
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-6 w-6 rounded-full bg-rose-50 border-rose-300 hover:bg-rose-100"
              onClick={handleCancel}
              disabled={saving}
              data-testid={`inline-status-cancel-${contract.id}`}
              title="Cancelar"
            >
              <X className="w-3 h-3 text-rose-700" />
            </Button>
          </>
        )}
      </div>

      {/* Motivo modal */}
      <Dialog open={motivoOpen} onOpenChange={(open) => { if (!saving) setMotivoOpen(open); if (!open) { setDraft(contract.status); setMotivoFiles([]); } }}>
        <DialogContent data-testid="motivo-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Motivo obrigatório
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              A alterar o estado para{' '}
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${current.className}`}>
                {current.label}
              </span>
              . Por favor indica o motivo — fica registado no histórico do contrato.
            </p>
            <div>
              <Label htmlFor="motivo-txt" className="text-sm">Motivo *</Label>
              <Textarea
                id="motivo-txt"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={4}
                placeholder="Ex: falta cópia do BI / cliente quer rever condições / ..."
                autoFocus
                data-testid="motivo-textarea"
              />
            </div>
            <div>
              <Label className="text-sm flex items-center justify-between">
                <span>Anexos (opcional)</span>
                <FlowAttachInput files={motivoFiles} onChange={setMotivoFiles} disabled={saving} />
              </Label>
              <p className="text-xs text-slate-400 mt-1">Imagens/PDFs como prova (ex: print da E-redes). Vão para o Fluxo do contrato.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setMotivoOpen(false); setDraft(contract.status); setMotivoFiles([]); }}
              disabled={saving}
              data-testid="motivo-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmMotivo}
              disabled={saving || !motivo.trim()}
              className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2"
              data-testid="motivo-confirm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Confirmar alteração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
